import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  ListItemIcon
} from '@mui/material';
import {
  Shield as PrivacyIcon,
  Security as SecurityIcon,
  Lock as DataIcon,
  Visibility as TransparencyIcon,
  Update as UpdateIcon,
  Email as ContactIcon
} from '@mui/icons-material';
import SEOHead from './common/SEOHead';

const PrivacyPolicy: React.FC = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const privacyStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - altifi",
    "description": "Privacy Policy for altifi personal finance management application. Learn how we protect and handle your financial data with industry-leading security measures.",
    "url": "https://altifi.com/privacy-policy",
    "isPartOf": {
      "@type": "WebSite",
      "name": "altifi",
      "url": "https://altifi.com"
    },
    "provider": {
      "@type": "Organization",
      "name": "altifi"
    }
  };

  return (
    <>
      <SEOHead 
        title="Privacy Policy - altifi | Data Protection and Security"
        description="Privacy Policy for altifi personal finance management application. Learn how we protect and handle your financial data with industry-leading security measures."
        keywords="privacy policy, data protection, financial data security, altifi, personal finance, data privacy, GDPR, user rights"
        canonicalUrl="https://altifi.com/privacy-policy"
        structuredData={privacyStructuredData}
      />
      <Box
        sx={{
          minHeight: '100vh',
          background: (theme) => theme.palette.mode === 'light'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[900]} 100%)`,
          py: 4
        }}
      >
        <Container maxWidth="md">
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              background: (theme) => theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(30, 30, 30, 0.9)',
              backdropFilter: 'blur(20px)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 3
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <PrivacyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h3" component="h1" fontWeight={700}>
                  Privacy Policy
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Last updated: {currentDate}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" paragraph>
              At <strong>altifi</strong>, protecting your privacy and securing your financial data is our top priority. 
              This Privacy Policy explains how we collect, use, protect, and handle your personal information.
            </Typography>

            <Alert 
              severity="info" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  fontSize: '0.95rem'
                }
              }}
            >
              <strong>Key Principle:</strong> We never access your bank accounts or financial institutions. 
              All financial data is manually entered by you, giving you complete control over your information.
            </Alert>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip icon={<SecurityIcon />} label="Bank-Level Security" size="small" />
              <Chip icon={<DataIcon />} label="Your Data, Your Control" size="small" />
              <Chip icon={<TransparencyIcon />} label="Full Transparency" size="small" />
              <Chip icon={<UpdateIcon />} label="Regular Updates" size="small" />
            </Box>
          </Paper>

          {/* Privacy Content */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: (theme) => theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(30, 30, 30, 0.9)',
              backdropFilter: 'blur(20px)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 3
            }}
          >
            {/* 1. Information We Collect */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
              1. Information We Collect
            </Typography>
            
            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2, fontWeight: 500 }}>
              Account Information
            </Typography>
            <Typography variant="body1" paragraph>
              When you create an account, we collect:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Email address (used for login and communication)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Name (for personalization)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Password (securely hashed and encrypted)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Profile picture (if provided via Google OAuth)" />
              </ListItem>
            </List>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2, fontWeight: 500 }}>
              Financial Data
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Important:</strong> All financial data is manually entered by you. We collect:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Budget categories and amounts you create" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Income and expense transactions you add" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Financial goals and targets you set" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Transaction descriptions and notes you write" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 2. How We Use Your Information */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              2. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use your information exclusively for providing and improving our service:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Service Provision" 
                  secondary="To provide budgeting, tracking, and financial management features"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Account Management" 
                  secondary="To create, maintain, and secure your account"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Communication" 
                  secondary="To send important account updates, security notices, and support responses"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Security" 
                  secondary="To detect and prevent unauthorized access and fraudulent activity"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 3. Information Sharing and Disclosure */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              3. Information Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>We do not sell, rent, or share your personal information with third parties</strong> except in these limited circumstances:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Legal Requirements" 
                  secondary="When required by law, court order, or government request"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Security Protection" 
                  secondary="To protect our rights, property, or safety, or that of our users"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Service Providers" 
                  secondary="With trusted partners who help operate our service (under strict confidentiality agreements)"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Business Transfer" 
                  secondary="In connection with a merger, acquisition, or sale of assets (users will be notified)"
                />
              </ListItem>
            </List>

            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
              <strong>No Financial Institution Access:</strong> We never connect to or share data with banks, 
              credit card companies, or other financial institutions. Your banking information remains completely private.
            </Alert>

            <Divider sx={{ my: 3 }} />

            {/* 4. Data Security */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              4. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement multiple layers of security to protect your data:
            </Typography>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2, fontWeight: 500 }}>
              Technical Safeguards
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Encryption in Transit" 
                  secondary="All data transmitted using HTTPS/TLS encryption"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Encryption at Rest" 
                  secondary="Financial data encrypted in our secure database"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Password Security" 
                  secondary="Passwords hashed using industry-standard bcrypt"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Secure Infrastructure" 
                  secondary="Hosted on secure, monitored cloud infrastructure"
                />
              </ListItem>
            </List>

            <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2, fontWeight: 500 }}>
              Access Controls
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Limited Access" 
                  secondary="Only authorized personnel have access to systems"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Regular Audits" 
                  secondary="Security practices reviewed and updated regularly"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Session Management" 
                  secondary="Automatic logout and secure session handling"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 5. Your Rights and Choices */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              5. Your Rights and Choices
            </Typography>
            <Typography variant="body1" paragraph>
              You have complete control over your data:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Access Your Data" 
                  secondary="View all your personal and financial data through your account dashboard"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Modify Your Data" 
                  secondary="Edit, update, or delete any financial information at any time"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Export Your Data" 
                  secondary="Download your financial data for your records"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Delete Your Account" 
                  secondary="Permanently delete your account and all associated data"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Opt-out of Communications" 
                  secondary="Unsubscribe from non-essential emails (security notifications will continue)"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 6. Data Retention */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              6. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your data as follows:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Active Accounts" 
                  secondary="Data retained as long as your account is active"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Account Deletion" 
                  secondary="Data permanently deleted within 30 days of account deletion"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Inactive Accounts" 
                  secondary="Accounts inactive for 3 years may be archived or deleted"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Legal Requirements" 
                  secondary="Some data may be retained longer if required by law"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 7. Cookies and Tracking */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              7. Cookies and Tracking
            </Typography>
            <Typography variant="body1" paragraph>
              We use minimal tracking for essential functionality:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Essential Cookies" 
                  secondary="Required for login, security, and basic functionality"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Performance Cookies" 
                  secondary="Anonymous usage analytics to improve the service"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="No Third-party Tracking" 
                  secondary="We do not use advertising or marketing trackers"
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 8. Children's Privacy */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              8. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our service is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If we become aware that we have collected personal 
              information from a child under 13, we will take steps to delete such information.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 9. International Users */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              9. International Users
            </Typography>
            <Typography variant="body1" paragraph>
              If you are accessing our service from outside the United States, please be aware that your 
              information may be transferred to, stored, and processed in the United States. By using our 
              service, you consent to such transfer and processing.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>GDPR Compliance:</strong> For users in the European Union, we comply with the General 
              Data Protection Regulation (GDPR) and provide all rights outlined in this policy.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 10. Changes to This Policy */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              10. Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy periodically. When we make significant changes, we will:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Notify you via email or in-app notification" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Update the 'Last updated' date at the top of this page" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide a summary of changes for major updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Give you the opportunity to review changes before they take effect" />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            {/* 11. Contact Us */}
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              11. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <ContactIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Privacy: privacy@altifi.com" 
                  secondary="For privacy-related questions and data requests"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ContactIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Support: support@altifi.com" 
                  secondary="For account and technical support"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ContactIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Data Protection Officer: dpo@altifi.com" 
                  secondary="For GDPR and data protection matters"
                />
              </ListItem>
            </List>

            <Box 
              sx={{ 
                mt: 4, 
                p: 3, 
                background: (theme) => theme.palette.mode === 'light' 
                  ? theme.palette.grey[50] 
                  : theme.palette.grey[900],
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`
              }}
            >
              <Typography variant="body2" color="text.secondary" align="center" paragraph>
                <strong>Your Trust Matters:</strong> We understand that financial data is deeply personal. 
                Our commitment to privacy and security isn't just a policy—it's fundamental to who we are.
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Thank you for trusting altifi with your financial journey. We're here to help you achieve 
                your financial goals while keeping your data safe and secure.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy; 